import csiImage from "csi/image/_base";
import _TemplatedMixin from "dijit/_TemplatedMixin";
import _WidgetBase from "dijit/_WidgetBase";
import template from "dojo/text!./ImageContainer.html";
import declare from "dojo/_base/declare";

const ImageContainer = declare([_WidgetBase, _TemplatedMixin], {
	baseClass: "csi-turntable-image-container",
	templateString: template,

	// attach point
	imageCacheNode: null,
	imageNode: null,
	iconNode: null,
	imagePlaceholderNode: null,

	/** @property {string} imageClassName - css class name to be applied to the image instead of base class name*/
	imageClass: "",

	/** @property {boolean} disableTurntable - disable mouse actions on turntable */
	disableTurntable: false,

	/** @property {boolean} isTurntable - indicate whether it is a singular image or a turntable */
	isTurntable: false,

	/** @property {number} index - current index being displayed */
	index: -1,

	/** @property {string[]} imageList - sequence of image sources to be shown (length 1: image, length > 1: turntable) */
	imageList: null,

	/** @property {boolean} - isImageCached - true if image cache is created */
	isImageCached: false,

	/** @property {Deferred} deferred - Deferred object corresponding to currently displayed image */
	deferred: null,

	/** @property {boolean} paddedImage - indicate whether image is padded or not. used for setting spacing on turntable icon*/
	paddedImage: false,

	/** @property {number} - predicted sizes of image, used to prevent frozen table row height flicker */
	predictedHeight: 0,
	predictedWidth: 0,

	/** refresh the displayed image when either index or imageList is modified */
	reloadImage() {
		if (this.imageList) {
			this.deferred = csiImage.create(null, this.imageList[Math.max(0, this.index)], this.imageNode).then((img: HTMLElement) => {
				this.imagePlaceholderNode?.remove();
				return img;
			});
		}
	},

	/** preload turntable images */
	createImageCache() {
		const cacheNode = this.imageCacheNode;
		// remove old cached images
		while (cacheNode.firstChild) {
			cacheNode.removeChild(cacheNode.firstChild);
		}
		// create new cache
		this.imageList.map((imgSrc:string)=>{
			const img = new Image(0, 0);
			img.src=imgSrc;
			cacheNode.appendChild(img);
		});
		this.isImageCached = true;
	},

	/** this is needed to satisfy Typescript */
	_set(attr: string, value: any) {
		this.inherited(arguments);
	},

	/** reload container with new imageList */
	_setImageListAttr(imageList: string | string[]) {
		this._set("imageList", [].concat(imageList));
		this.reloadImage();
		this.isImageCached = false;
	},

	/** allow to use custom css for image or fall back to baseClass if imageClass is not specified  */
	_setBaseClassAttr(baseClass: string) {
		if (!this.imageClass) {
			const cssClass = baseClass + "-image";
			this.imageClass = cssClass;
			this.imageNode.className = cssClass;
		}
		this.containerNode.classList.add("csi-turntable-" + this.imageClass);
		this._set("baseClass", baseClass);
	},

	/** show turntable icon if image is a turntable */
	_setIsTurntableAttr(turntable: boolean) {
		this.iconNode.style.display = turntable ? "flex" : "none";
		this._set("isTurntable", turntable);
	},

	/** Update image cache and src of img tag to be that of the next index */
	_setIndexAttr(nextIndex: number) {
		this._set("index", nextIndex);
		if (this.isTurntable && !this.isImageCached && (this.imageList?.length > 1) && (this.index > 0)) {
			this.createImageCache();
		}
		this.reloadImage();
	},

	/** Modifies spacing of turntable icon depending on whether image is padded or not (per figma) */
	_setPaddedImage(padded: boolean) {
		if (padded) {
			this.iconNode.style.right = "4px";
			this.iconNode.style.bottom = "4px";
		}
		this._set("paddedImage", padded);
	},

	/** Handler for onmouseleave event */
	_onMouseLeave(e: MouseEvent) {
		this._setIndexAttr(-1);
	},

	/** Handler for onmousemove event, performs index calculations based on mouse position */
	_onMouseMove(e: MouseEvent) {
		const target = e.target as HTMLDivElement;

		// handles edge case of hovering over the icon where x position is not as expected
		if (this.disableTurntable || !this.isTurntable || target.nodeName == "svg" || target.nodeName == "path") {
			return;
		}

		let mouseX: number;
		let width: number;

		if (target.nodeName == "IMG") {
			mouseX = e.offsetX + target.offsetLeft;
			const offsetParent = target.offsetParent as HTMLDivElement;
			width = offsetParent.offsetWidth;
		} else if (e.target === this.domNode) {
			mouseX = e.offsetX;
			width = target.clientWidth;
		} else {
			// prevent reacting on mouse move over other elements, which can present on top of Image Container
			return;
		}

		const midpoint = Math.floor(width / 2);
		const numImages = this.imageList.length;
		const indexWidth = width / (numImages + 1);
		const widthsFromMidpoint = (mouseX - midpoint) / indexWidth;

		const nextIndex = widthsFromMidpoint >= 0 ? Math.floor(widthsFromMidpoint) : Math.ceil(numImages + widthsFromMidpoint) % numImages;

		// prevent excess calls
		if (nextIndex != this.index && (nextIndex === 0 || this.index !== -1)) {
			this._setIndexAttr(nextIndex);
		}
	},
});

export = ImageContainer;
